/* eslint-disable no-nested-ternary */
import React, { ReactNode, useCallback, useEffect } from 'react';
import Head from 'next/head';
import useCookieConsent from '@hooks/useCookieConsent';
import TagManager from '@utils/tags';
import SkinnyBanner from '@components/CaptureEmail/SkinnyBanner/SkinnyBanner';
import { Location } from '@components/Hotels/types';
import UserLocationProvider from '@context/GeoIpDetectionContext';
import Footer from '@components/Footer/Footer';
import { CAPTURE_EMAIL_VIEWED, EMAIL_CAPTURED } from '@constants/amplitudeEvents';
import { useEvents } from '@events/EventsProvider';
import { useRouter } from 'next/router';

type Props = {
  children: ReactNode;
  userLocation: Location | null;
  showSkinnyBanner?: boolean;
  showFooter?: boolean;
};

export default function SimpleLayout({
  children,
  userLocation = null,
  showSkinnyBanner = true,
  showFooter = true,
}: Props) {
  const { allowCookies } = useCookieConsent();
  const { track } = useEvents();
  const router = useRouter();

  const removeMarketingModuleURLParam = useCallback(() => {
    const updatedQuery = { ...router.query };
    delete updatedQuery['marketing-module'];

    router.push(
      {
        pathname: router.pathname,
        query: updatedQuery,
      },
      undefined,
      { shallow: true, scroll: false },
    );
  }, [router]);

  // Initialize 3rd party scripts if cookies allowed
  useEffect(() => {
    if (allowCookies) {
      TagManager.initialize();
    }
  }, [allowCookies]);

  // Listen for any PostMessage events from Digioh
  useEffect(() => {
    const handlePostMessage = (event: MessageEvent) => {
      if (event.data?.type === 'digiohEmailCaptured') {
        track(EMAIL_CAPTURED, {
          source: router.query['marketing-module'],
          email: event.data.value,
        });
      }

      if (event.data?.type === 'digiohBoxClosed') {
        removeMarketingModuleURLParam();
      }

      if (event.data?.type === 'digiohBoxDisplayed') {
        track(CAPTURE_EMAIL_VIEWED, {
          source: 'PopUpOnLoad',
        });
      }
    };

    window.addEventListener('message', handlePostMessage);

    return () => {
      window.removeEventListener('message', handlePostMessage);
    };
  }, [removeMarketingModuleURLParam, router.query, track]);

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=5, viewport-fit=cover"
        />
      </Head>

      {showSkinnyBanner ? <SkinnyBanner /> : null}

      <UserLocationProvider value={userLocation}>
        <>
          {children}

          {showFooter ? <Footer /> : null}
        </>
      </UserLocationProvider>
    </>
  );
}
